import React, { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import authAPI from "../api/auth";

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [tokenUsed, setTokenUsed] = useState(false); // Track if token has been used
  
  const token = searchParams.get("token");

  useEffect(() => {
    console.log("[RESET PASSWORD] Component mounted, token:", token ? "present" : "missing");
    if (!token) {
      console.error("[RESET PASSWORD] No token found in URL");
      setError("Invalid reset link. Please request a new password reset.");
      // Don't redirect immediately - let user see the error
      setTimeout(() => {
        console.log("[RESET PASSWORD] Redirecting to login due to missing token");
        navigate("/login", { 
          state: { 
            error: true,
            message: "Invalid reset link. Please request a new password reset." 
          } 
        });
      }, 3000);
    }
  }, [token, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    // Validate password strength (matching registration requirements)
    const passwordErrors = [];
    if (password.length < 8) passwordErrors.push("At least 8 characters");
    if (password.length > 20) passwordErrors.push("Maximum 20 characters");
    if (!/[A-Z]/.test(password)) passwordErrors.push("At least one uppercase letter");
    if (!/[0-9]/.test(password)) passwordErrors.push("At least one number");
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) passwordErrors.push("At least one special character");
    
    if (passwordErrors.length > 0) {
      setError(`Password requirements: ${passwordErrors.join(", ")}`);
      return;
    }

    // Prevent multiple submissions with the same token
    if (tokenUsed || isLoading) {
      console.log("[RESET PASSWORD] Already processing, ignoring duplicate submission");
      return;
    }

    setIsLoading(true);
    setTokenUsed(true); // Mark token as being used to prevent reuse

    try {
      console.log("[RESET PASSWORD] Submitting password reset request...");
      const response = await authAPI.resetPassword(token, password);
      console.log("[RESET PASSWORD] Password reset successful:", response);
      setMessage(response.message || "Password has been reset successfully. Redirecting to login...");
      
      // Disable form immediately after success
      setTokenUsed(true);
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        console.log("[RESET PASSWORD] Redirecting to login after successful reset");
        navigate("/login", { 
          state: { 
            message: "Password has been reset successfully! You can now login with your new password." 
          } 
        });
      }, 3000);
    } catch (err) {
      console.error("[RESET PASSWORD] Error resetting password:", err);
      const errorMessage = err.response?.data?.detail || err.message || "Failed to reset password. Please try again.";
      setError(errorMessage);
      
      // If token is invalid, expired, or already used, don't allow retry
      if (errorMessage.includes("Invalid") || errorMessage.includes("expired") || errorMessage.includes("already been used")) {
        setTokenUsed(true); // Prevent further attempts with this token
        setTimeout(() => {
          console.log("[RESET PASSWORD] Redirecting to login due to invalid/expired/used token");
          navigate("/login", { 
            state: { 
              error: true,
              message: errorMessage 
            } 
          });
        }, 5000);
      } else {
        // For other errors (network, etc.), allow retry
        setTokenUsed(false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="card auth-card">
        <h2 className="page-title">Set New Password</h2>
        
        {message ? (
          <div>
            <div style={{color:"#059669",fontSize:14,marginBottom:16,padding:12,background:"#d1fae5",borderRadius:8,border:"1px solid #6ee7b7"}}>
              {message}
            </div>
            <div style={{color:"#64748b",fontSize:12}}>
              Redirecting to login page...
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={{marginBottom:12}}>
              <label className="form-label">
                New Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                maxLength="20"
                style={{width:"100%",borderRadius:8,background:"#ffffff",border:"1px solid #cbd5e1",color:"#1e293b",padding:"12px",fontSize:"14px"}}
                placeholder="Enter new password (8-20 chars, uppercase, number, special char)"
              />
              <div style={{color:"#64748b",fontSize:11,marginTop:4}}>
                Requirements: 8-20 characters, 1 uppercase, 1 number, 1 special character
              </div>
            </div>
            
            <div style={{marginBottom:16}}>
              <label className="form-label">
                Confirm Password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                maxLength="20"
                style={{width:"100%",borderRadius:8,background:"#ffffff",border:"1px solid #cbd5e1",color:"#1e293b",padding:"12px",fontSize:"14px"}}
                placeholder="Confirm new password"
              />
            </div>
            
            {error && (
              <div style={{color:"#dc2626",fontSize:12,marginBottom:12,padding:8,backgroundColor:"#fef2f2",borderRadius:6,border:"1px solid #fecaca"}}>
                {error}
              </div>
            )}
            
            <button
              type="submit"
              disabled={isLoading || !token || tokenUsed}
              style={{
                width:"100%",
                padding:"12px",
                borderRadius:8,
                background:(isLoading || !token || tokenUsed) ? "#94a3b8" : "#3b82f6",
                color:"#fff",
                fontWeight:600,
                border:"none",
                cursor:(isLoading || !token || tokenUsed) ? "not-allowed" : "pointer",
                fontSize:"14px"
              }}
            >
              {isLoading ? "Resetting..." : tokenUsed ? "Token Already Used" : "Reset Password"}
            </button>
          </form>
        )}

        <div style={{marginTop:16,textAlign:"center"}}>
          <Link 
            to="/login" 
            style={{color:"#3b82f6",fontSize:14,textDecoration:"none"}}
          >
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;



